import { useEffect } from "react";
import moment from "moment";
import momentTZ from "moment-timezone";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { getFlightData } from "api";
import { useGetAirlines } from "api/queries/common/useGetAirlines";
import { useGetAirports } from "api/queries/common/useGetAirports";
import { useGetFlightData } from "api/queries/common/useGetFlightData";
import {
  IEditScheduleForm,
  IEditSchedulePayload,
  IEditScheduleStopForm,
  IEditScheduleVesselForm,
} from "components/molecules/TopNav/provider/EditScheduleDialog/types";
import { useGetShipmentSchedule } from "api/queries/shipments/useGetShipmentSchedule";
import { getDateTimeInEnglish } from "./dateTimeHelper";

export const mapAirlinesToOptions = (data: { name: string; api_code: string }) => ({
  label: `${data.name} (${data.api_code})`,
  value: data.api_code,
});

export const mapAirportsToOptions = (data: {
  code: string;
  name: string;
  city_name: string;
  country_code: string;
  country_name: string;
}) => ({
  label: `(${data.code}) ${[data.name, data.city_name, data.country_code].filter(Boolean).join(", ")}`,
  value: data.code,
  city_name: data.city_name,
  country_name: data.country_name,
});

export type SeaportOptionType = {
  label: string;
  value: string;
  un_locode: string;
  city_name: string;
  country_iso_code: string;
};

export const mapSeaportsToOptions = (data: {
  name: string;
  un_locode: string;
  city_name: string;
  country_name: string;
  country_code: string;
  country_iso_code: string;
}): SeaportOptionType => ({
  label: `(${data.un_locode}) ${[data.city_name, data.country_name, data.country_code].filter(Boolean).join(", ")}`,
  value: data.name,
  un_locode: data.un_locode,
  city_name: data.city_name,
  country_iso_code: data.country_iso_code,
});

const normalizeScheduleTime = (time?: string) => {
  if (!time) return "";
  const m = moment(time, ["HH:mm:ss", "HH:mm"], true);
  return m.isValid() ? m.format("HH:mm:ss") : time;
};

const mapStopRowFromSchedule = (stop: any): IEditScheduleStopForm => {
  const updatedDate = stop?.updated_date ?? stop?.quoted_date ?? "";
  const updatedTime = normalizeScheduleTime(stop?.updated_time ?? stop?.quoted_time);

  return {
    ...stop,
    updated_date: typeof updatedDate === "string" ? updatedDate.split("T")[0] : updatedDate,
    updated_time: updatedTime,
  };
};

const buildMultistopPortDisplay = ({
  code,
  cityOrState,
  countryName,
  countryIsoCode,
  fallbackName,
}: {
  code?: string;
  cityOrState?: string;
  countryName?: string;
  countryIsoCode?: string;
  fallbackName?: string;
}) => {
  const locationParts = [cityOrState, countryName, countryIsoCode].filter(Boolean).join(", ");
  if (code && locationParts) return `(${code}) ${locationParts}`;
  if (code) return `(${code})`;
  if (locationParts) return locationParts;
  return fallbackName ?? "";
};

const mapVesselRowFromSchedule = (v: any): IEditScheduleVesselForm => ({
  id: v.id,
  vessel_number: v.updated_vessel_number ?? v.vessel_number ?? "",
  delivery_date: v.updated_delivery_date ?? v.vessel_departure_date ?? v.delivery_date ?? "",
  vessel_arrival_date: v.updated_vessel_arrival_date ?? v.vessel_arrival_date ?? "",
  departure_port_code: v.updated_departure_port ?? v.departure_port_code ?? v.departure_port ?? "",
  departure_city: v.updated_departure_city ?? v.departure_city ?? "",
  departure_country_code:
    v.updated_departure_country_code ?? v.departure_country_code ?? v.updated_departure_country_iso_code ?? "",
  departure_country_name: v.updated_departure_country_name ?? v.departure_country_name ?? "",
  departure_port_name: buildMultistopPortDisplay({
    code: v.updated_departure_port ?? v.departure_port_code ?? v.departure_port,
    cityOrState: v.updated_departure_city ?? v.departure_city ?? v.updated_departure_state ?? v.departure_state,
    countryName: v.updated_departure_country_name ?? v.departure_country_name,
    countryIsoCode:
      v.updated_departure_country_code ??
      v.departure_country_code ??
      v.updated_departure_country_iso_code ??
      v.departure_country_iso_code,
    fallbackName: v.updated_departure_port_name ?? v.departure_port_name,
  }),
  arrival_port_code: v.updated_arrival_port ?? v.arrival_port_code ?? v.arrival_port ?? "",
  arrival_city: v.updated_arrival_city ?? v.arrival_city ?? "",
  arrival_country_code: v.updated_arrival_country_code ?? v.arrival_country_code ?? v.updated_arrival_country_iso_code ?? "",
  arrival_country_name: v.updated_arrival_country_name ?? v.arrival_country_name ?? "",
  arrival_port_name: buildMultistopPortDisplay({
    code: v.updated_arrival_port ?? v.arrival_port_code ?? v.arrival_port,
    cityOrState: v.updated_arrival_city ?? v.arrival_city ?? v.updated_arrival_state ?? v.arrival_state,
    countryName: v.updated_arrival_country_name ?? v.arrival_country_name,
    countryIsoCode:
      v.updated_arrival_country_code ??
      v.arrival_country_code ??
      v.updated_arrival_country_iso_code ??
      v.arrival_country_iso_code,
    fallbackName: v.updated_arrival_port_name ?? v.arrival_port_name,
  }),
});

export const prefillFormFromSchedule = (
  shipmentScheduleData: any,
  options?: { isMultiStop?: boolean },
): IEditScheduleForm => {
  if (options?.isMultiStop) {
    const stops = (shipmentScheduleData?.stops || []).map(mapStopRowFromSchedule);
    const vessels = (shipmentScheduleData?.vessels || []).map(mapVesselRowFromSchedule);
    const flights = shipmentScheduleData?.flights || [];

    return {
      pickup_date: "",
      pickup_time: "",
      estimated_delivery_date: "",
      estimated_delivery_time: "",
      stops,
      vessels,
      flights,
    };
  }

  return {
    ...shipmentScheduleData,
    vessel_imo: shipmentScheduleData?.vessel_number,
    departure_city_name: shipmentScheduleData?.departure_city,
    departure_country_iso_code: shipmentScheduleData?.departure_country_code,
    departure_port_locode: shipmentScheduleData?.departure_port_code,
    arrival_port_locode: shipmentScheduleData?.arrival_port_code,
    arrival_city_name: shipmentScheduleData?.arrival_city,
    arrival_country_iso_code: shipmentScheduleData?.arrival_country_code,
    departure_date: shipmentScheduleData?.vessel_departure_date,
    arrival_date: shipmentScheduleData?.vessel_arrival_date,

    estimated_delivery_date: shipmentScheduleData?.delivery_date,
    estimated_delivery_time: shipmentScheduleData?.delivery_time,
  };
};

const mapFlightsForEditSchedulePayload = (flights: IEditScheduleForm["flights"]) =>
  (flights || []).map((flight) => ({
    ...flight,
    from_airport_code: flight.from_airport_code ?? "",
    to_airport_code: flight.to_airport_code ?? "",
  }));

const mapVesselsForMultistopPayload = (vessels: IEditScheduleVesselForm[] | undefined): IEditSchedulePayload["vessels"] =>
  (vessels || [])
    .filter((v) => {
      if (v.id) return true;

      const hasVesselNumber = Boolean(v.vessel_number?.trim());
      const hasOtherVesselData = Boolean(
        v.delivery_date ||
          v.vessel_arrival_date ||
          v.departure_port_code ||
          v.arrival_port_code ||
          v.departure_country_code ||
          v.departure_city ||
          v.arrival_country_code ||
          v.arrival_city,
      );

      return hasVesselNumber || hasOtherVesselData;
    })
    .map((v) => {
      const row: NonNullable<IEditSchedulePayload["vessels"]>[number] = {
        vessel_number: v.vessel_number,
        departure_port_code: v.departure_port_code,
        arrival_port_code: v.arrival_port_code,
        delivery_date: v.delivery_date,
        vessel_arrival_date: v.vessel_arrival_date,
        departure_country_code: v.departure_country_code,
        departure_city: v.departure_city,
        arrival_country_code: v.arrival_country_code,
        arrival_city: v.arrival_city,
      };
      if (v.id) {
        row.id = v.id;
      }
      return row;
    });

/**
 * Delivery datetimes must be after the first pickup datetime selected in the form.
 */
export const validateMultistopSchedule = (values: IEditScheduleForm): string | null => {
  const stops = values.stops || [];

  const firstPickup = stops.find((s) => s.stop_type === "Pickup");
  if (!firstPickup?.updated_date || !firstPickup?.stop_timezone) return null;

  const firstPickupTime = normalizeScheduleTime(firstPickup.updated_time) || "00:00:00";
  const firstPickupDatetime = momentTZ.tz(
    `${firstPickup.updated_date} ${firstPickupTime}`,
    "YYYY-MM-DD HH:mm:ss",
    firstPickup.stop_timezone,
  );
  if (!firstPickupDatetime.isValid()) return null;

  const deliveryStops = stops.filter((s) => s.stop_type !== "Pickup");

  const invalidDelivery = deliveryStops.find((stop) => {
    const tz = stop.stop_timezone;
    const date = stop.updated_date;
    const time = stop.updated_time || "00:00:00";
    if (!date || !tz) {
      return false;
    }

    const deliveryMoment = momentTZ.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm:ss", tz);
    if (!deliveryMoment.isValid()) {
      return false;
    }

    return !deliveryMoment.isAfter(firstPickupDatetime);
  });

  if (invalidDelivery) {
    return "Delivery schedule should be after the first pickup schedule selected in the form (timezone is considered).";
  }

  return null;
};

export const cleanFormValuesForPayload = (
  values: IEditScheduleForm,
  {
    isFlight,
    isOcean,
    isPoPSubmitted,
    isMultiStop,
  }: { isFlight?: boolean; isOcean?: boolean; isPoPSubmitted?: boolean; isMultiStop?: boolean },
): IEditSchedulePayload => {
  const { estimated_delivery_date, estimated_delivery_time, pickup_date, pickup_time, flights = [] } = values;

  if (isMultiStop) {
    const stopsPayload = (values.stops || []).map((s) => ({
      id: s.id,
      updated_date: s.updated_date,
      updated_time: normalizeScheduleTime(s.updated_time),
    }));

    const payload: IEditSchedulePayload = {
      stops: stopsPayload,
    };

    if (isFlight) {
      payload.flights = mapFlightsForEditSchedulePayload(flights);
    }

    if (isOcean) {
      // Always send `vessels` (like we always send `flights` for air) so removing the last
      // vessel still reaches the API; omitting the key would leave schedule/vessel rows unchanged.
      payload.vessels = mapVesselsForMultistopPayload(values.vessels);
    }

    return payload;
  }

  const flightValues = isFlight && flights?.length > 0 ? mapFlightsForEditSchedulePayload(flights) : [];

  const {
    departure_date,
    departure_time,
    arrival_date,
    arrival_time,
    vessel_imo,
    departure_port_locode,
    departure_port_name,
    departure_city_name,
    departure_country_iso_code,
    arrival_port_name,
    arrival_port_locode,
    arrival_city_name,
    arrival_country_iso_code,
  } = values;
  const oceanValues = isOcean
    ? {
        departure_date,
        departure_time,
        arrival_date,
        arrival_time,
        vessel_imo,
        departure_port_locode,
        departure_port_name,
        departure_city_name,
        departure_country_iso_code,
        arrival_port_name,
        arrival_port_locode,
        arrival_city_name,
        arrival_country_iso_code,
      }
    : {};

  return {
    ...(isFlight && { flights: flightValues }),
    ...(isOcean && { ...oceanValues }),
    estimated_delivery_date,
    estimated_delivery_time,
    ...(!isPoPSubmitted && { pickup_date, pickup_time }),
  };
};

interface getAirlineAndPortOptionsParams {
  airlinesQuery: string;
  departurePortQuery: string;
  arrivalPortQuery: string;
  index: number;
}

export const useGetAirlineAndPortOptions = ({
  airlinesQuery,
  departurePortQuery,
  arrivalPortQuery,
  index,
}: getAirlineAndPortOptionsParams) => {
  const id = useParams().id?.toUpperCase()!;
  const {
    data: { flights = {} },
  }: any = useGetShipmentSchedule(id, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const hasPrefilledData = flights && index < flights.length;
  const currentData = hasPrefilledData ? flights[index] : {};

  const { watch, clearErrors, setError, setValue, getValues } = useFormContext();
  const [isManual, airlineCode, flightNumber, departureDate, route, isDelete] = watch(
    ["is_manual", "airline_code", "flight_number", "departure_date", "route", "delete"].map(
      (fieldname) => `flights.${index}.${fieldname}` as keyof IEditScheduleForm["flights"],
    ),
  );

  const { data: airlinesData } = useGetAirlines(airlinesQuery);
  const { data: departurePortsData } = useGetAirports(departurePortQuery);
  const { data: arrivalPortsData } = useGetAirports(arrivalPortQuery);

  // Prefill options fetched for /schedule endpoint
  const airlinePrefilledOptions = hasPrefilledData
    ? [{ name: currentData.airline_name, api_code: currentData.airline_code }].map(mapAirlinesToOptions)
    : [];
  const departureAirportPrefilledOptions = hasPrefilledData
    ? [
        {
          code: currentData.departure_airport_code,
          name: currentData.departure_airport_name,
          city_name: currentData.departure_airport_city_name,
          country_code: currentData.departure_airport_country_code,
          country_name: currentData.departure_airport_country_name,
        },
      ].map(mapAirportsToOptions)
    : [];

  const arrivalAirportPrefilledOptions = hasPrefilledData
    ? [
        {
          code: currentData.arrival_airport_code,
          name: currentData.arrival_airport_name,
          city_name: currentData.arrival_airport_city_name,
          country_code: currentData.arrival_airport_country_code,
          country_name: currentData.departure_airport_country_name,
        },
      ].map(mapAirportsToOptions)
    : [];

  // prettier-ignore
  const airlineOptions = airlinesQuery
  ? airlinesData?.map(mapAirlinesToOptions) ?? [] 
  : airlinePrefilledOptions;
  const departurePortOptions = departurePortQuery
    ? departurePortsData?.map(mapAirportsToOptions) ?? []
    : departureAirportPrefilledOptions;
  const arrivalPortOptions = arrivalPortQuery
    ? arrivalPortsData?.map(mapAirportsToOptions) ?? []
    : arrivalAirportPrefilledOptions;

  const airlineName = airlineOptions.find((option) => option.value === airlineCode)?.label;
  const enableFetchAutoFlightData =
    !isDelete && !isManual && Boolean(airlineName) && Boolean(flightNumber) && Boolean(departureDate);

  const autoFlightParams = {
    airline: airlineName,
    flight_number: flightNumber,
    departure_date: getDateTimeInEnglish(departureDate),
  };
  const autoFlightParamsWithRoute = {
    ...autoFlightParams,
    route,
  };

  function getAirportCode(name: string) {
    return name?.split("(")[1].split(")")[0];
  }

  const { data: autoFlightData, isFetching: isFetchingAutoFlightData } = useGetFlightData(
    {
      params: autoFlightParams,
    },
    {
      enabled: enableFetchAutoFlightData,
      queryFn: () =>
        getFlightData({ params: autoFlightParams })
          .then((res) => res.data)
          .then((data) => {
            clearErrors(`flights.${index}.error`);
            if (data.error && data.message) setError(`flights.${index}.error`, { type: "manual", message: data.message });
            if (data.error && !data.message)
              setError(`flights.${index}.error`, { type: "manual", message: "No route found" });
            return data;
          }),
    },
  );

  useEffect(() => {
    if (isManual) clearErrors(`flights.${index}.error`);
  }, [isManual]);

  useEffect(() => {
    const flightRows = getValues("flights");
    flightRows?.forEach((flightDetail: any, flightIndex: number) => {
      if (flightDetail.departure_airport_code)
        setValue(`flights.${flightIndex}.from_airport_code`, flightDetail.departure_airport_code);
      if (flightDetail.arrival_airport_code)
        setValue(`flights.${flightIndex}.to_airport_code`, flightDetail.arrival_airport_code);
      if (flightDetail.arrival_date) setValue(`flights.${flightIndex}.arrival_date`, flightDetail.arrival_date);
      if (flightDetail.arrival_time) setValue(`flights.${flightIndex}.arrival_time`, flightDetail.arrival_time);
      if (flightDetail.departure_time) setValue(`flights.${flightIndex}.departure_time`, flightDetail.departure_time);
    });
  }, []);

  const autoFlightOptions =
    typeof autoFlightData?.routes === "string"
      ? [{ label: autoFlightData.routes, value: autoFlightData.routes }]
      : Object.entries(autoFlightData?.routes ?? {}).map(([value, label]) => ({ value, label })) || [];

  const { data: autoFlightDataWithRoute, isFetching: isFetchingAutoFlightDataWithRoute } = useGetFlightData(
    {
      params: autoFlightParamsWithRoute,
    },
    {
      enabled: enableFetchAutoFlightData && Boolean(route),
      queryFn: () =>
        getFlightData({ params: autoFlightParamsWithRoute })
          .then((res) => res.data)
          .then((data) => {
            clearErrors(`flights.${index}.error`);
            const arrivalDate = getDateTimeInEnglish(data.arrival_date, { format: "YYYY-MM-DD" });
            setValue(`flights.${index}.arrival_date`, arrivalDate);
            setValue(`flights.${index}.arrival_time`, data.arrival_time);
            setValue(`flights.${index}.departure_time`, data.departure_time);
            setValue(`flights.${index}.from_airport_name`, data.departure_airport_text);
            setValue(`flights.${index}.from_airport_code`, getAirportCode(data.departure_airport_text));
            setValue(`flights.${index}.to_airport_name`, data.arrival_airport_text);
            setValue(`flights.${index}.to_airport_code`, getAirportCode(data.arrival_airport_text));
            if (data.error && data.message) setError(`flights.${index}.error`, { type: "manual", message: data.message });
            return data;
          }),
    },
  );

  const isFetchingRoute = isFetchingAutoFlightData || isFetchingAutoFlightDataWithRoute;

  return {
    airlineOptions,
    departurePortOptions,
    arrivalPortOptions,
    autoFlightData,
    autoFlightOptions,
    autoFlightDataWithRoute,
    hasPrefilledData,
    isFetchingRoute,
  };
};
